import React, { useState } from 'react';
import { api } from '../../api/config';
import { Box, Button, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Paper, CircularProgress, InputAdornment, useTheme } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import styled from '@emotion/styled';

const FormGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  width: '100%',
  marginBottom: '16px',
  '@media (min-width: 600px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '& > div': {
    minWidth: 0, // Prevent overflow
  },
});

const FullWidthField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& > fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover > fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.87)',
    },
  },
  '@media (max-width: 599px)': {
    '& .MuiInputBase-root': {
      fontSize: '16px', // Prevents zoom on mobile
    },
  },
});

const FullWidthFormControl = styled(FormControl)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& > fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover > fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.87)',
    },
  },
  '& .MuiInputLabel-root': {
    backgroundColor: 'white',
    padding: '0 4px',
  },
});

const initialState = {
  name: "",
  mobile: "",
  first_name: "",
  last_name: "",
  type: "retailer",
  pincode: "",
  city: "",
  state: "",
  address_line: "",
};

interface AccountCreationFormProps {
  mobile?: string;
  onSuccess: () => void;
}

const AccountCreationForm: React.FC<AccountCreationFormProps> = ({ mobile = "", onSuccess }) => {
  const theme = useTheme();
  const [form, setForm] = useState({ ...initialState, mobile });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Format the request data according to the expected API format
      const requestData = {
        name: form.name,
        mobile: form.mobile,
        first_name: form.first_name,
        last_name: form.last_name,
        type: form.type,
        pincode: form.pincode,
        city: form.city,
        state: form.state,
        address_line: form.address_line,
        domain: window.location.hostname,
        domain_name: window.location.hostname,
        x_domain: window.location.hostname
      };

      // Use the storefronts API endpoint for company creation
      const response = await api.post('/api/v1/storefront/storefronts/create-company/', requestData);
      
      // If we get a 201 status code, consider it a success regardless of response body
      if (response.status === 201) {
        onSuccess();
      } else if (response.data && response.data.success === false) {
        // Handle case where API returns success: false in the response body
        throw new Error(response.data.message || 'Account creation failed');
      } else {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Create Account Error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'center', color: theme.palette.primary.main }}>
          Create Your Account
        </Typography>
        
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        <FormGrid>
          <div style={{ gridColumn: '1 / -1' }}>
            <FullWidthField
              label="Firm Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
            />
          </div>
          <div>
            <FullWidthField
              label="Mobile"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">+91</InputAdornment>,
              }}
            />
          </div>
          <div>
            <FullWidthField
              label="First Name"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
            />
          </div>
          <div>
            <FullWidthField
              label="Last Name"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
            />
          </div>
          <div>
            <FullWidthFormControl margin="normal" size="small" required>
              <InputLabel>Account Type</InputLabel>
              <Select
                name="type"
                value={form.type}
                label="Account Type"
                onChange={handleChange}
                variant="outlined"
              >
                <MenuItem value="retailer">Retailer</MenuItem>
                <MenuItem value="play">Distributor</MenuItem>
              </Select>
            </FullWidthFormControl>
          </div>
          <div>
            <FullWidthField
              label="Pincode"
              name="pincode"
              type="number"
              value={form.pincode}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
              inputProps={{
                pattern: '\\d{6}',
                maxLength: 6,
              }}
            />
          </div>
          <div>
            <FullWidthField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
            />
          </div>
          <div>
            <FullWidthField
              label="State"
              name="state"
              value={form.state}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
            />
          </div>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FullWidthField
              label="Full Address"
              name="address_line"
              value={form.address_line}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </Box>
        </FormGrid>

        <Box sx={{ 
          mt: 3, 
          textAlign: 'center',
          '& button': {
            width: '100%',
            '@media (min-width: 600px)': {
              width: 'auto',
              minWidth: '200px',
            },
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 500,
          },
          mb: 3,
        }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)'
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Account'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AccountCreationForm;
